import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { imgSrc, fmtPrice, mergeContent } from "../lib/api";
import { useSite } from "../lib/SiteContext";

const EASE = [0.16, 1, 0.3, 1];
const SPANS = [
  "md:col-span-7",
  "md:col-span-5 md:mt-24",
  "md:col-span-5",
  "md:col-span-7 md:mt-24",
  "md:col-span-12",
];
const RATIOS = ["aspect-[4/5]", "aspect-[3/4]", "aspect-[3/4]", "aspect-[4/5]", "aspect-[21/10]"];

export default function GalleryGrid({ artworks, onSelect }) {
  const { settings } = useSite();
  const c = mergeContent(settings);
  const count = artworks.length;

  return (
    <section id="works" data-testid="works-section" className="scroll-mt-28 md:scroll-mt-0 mx-auto max-w-[1440px] px-6 md:px-10 py-24 md:py-36">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, amount: 0.15 }}
        transition={{ duration: 0.9, ease: EASE }}
        className="flex flex-wrap items-end justify-between gap-6"
      >
        <div>
          {c.collection_eyebrow && (
            <div className="flex items-center gap-4 mb-6">
              <span className="h-px w-12 rule-flare" />
              <p className="eyebrow !text-ochre/90">{c.collection_eyebrow}</p>
            </div>
          )}
          {c.collection_title && (
            <h2 className="font-display font-light text-4xl md:text-6xl leading-tight">{c.collection_title}</h2>
          )}
        </div>
        <p className="font-meta text-sm text-mute" data-testid="works-count">
          ({String(count).padStart(2, "0")})
        </p>
      </motion.div>

      {count === 0 ? (
        <div className="mt-16 border border-dashed border-white/15 py-24 text-center" data-testid="works-empty-state">
          <p className="font-display italic text-2xl text-bone/80">The walls are being hung.</p>
          <p className="font-meta text-[10px] uppercase tracking-[0.3em] text-mute mt-3">
            New works will appear here soon
          </p>
        </div>
      ) : (
        <div className="mt-14 md:mt-20 grid grid-cols-1 md:grid-cols-12 gap-x-8 gap-y-14">
          {artworks.map((a, i) => (
            <motion.article
              key={a.id}
              data-testid={`artwork-card-${a.id}`}
              onClick={() => onSelect(a)}
              className={`group cursor-pointer ${SPANS[i % 5]}`}
              initial={{ opacity: 0, y: 48 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{ duration: 0.8, ease: EASE, delay: (i % 2) * 0.08 }}
            >
              <div className={`relative overflow-hidden bg-panel border border-line ${RATIOS[i % 5]}`}>
                <img
                  src={imgSrc(a.image_path)}
                  alt={a.title}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1200ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.04]"
                  data-testid={`artwork-image-${a.id}`}
                />
                <span className="absolute top-3 left-3 font-meta text-[10px] bg-ink/70 backdrop-blur px-2 py-1 text-bone/90">
                  {String(i + 1).padStart(2, "0")}
                </span>
                {a.sold && (
                  <span
                    className="absolute top-3 right-3 bg-oxblood/90 backdrop-blur px-2 py-1 font-meta text-[9px] uppercase tracking-[0.2em] text-bone"
                    data-testid={`artwork-sold-${a.id}`}
                  >
                    Sold
                  </span>
                )}
                <div className="absolute inset-0 flex items-center justify-center bg-ink/40 opacity-0 group-hover:opacity-100 transition-opacity duration-500">
                  <span className="flex items-center gap-2 border border-ochre/60 bg-ink/70 backdrop-blur px-5 py-3 font-meta text-[10px] uppercase tracking-[0.3em] text-ochre">
                    View <ArrowUpRight size={12} />
                  </span>
                </div>
              </div>
              <div className="mt-4 flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h3 className="font-display text-xl md:text-2xl leading-snug">{a.title}</h3>
                  <p className="font-meta text-[10px] md:text-[11px] uppercase tracking-[0.18em] text-mute mt-2">
                    {[a.medium, a.dimensions].filter(Boolean).join(" · ")}
                  </p>
                </div>
                <div className="text-right shrink-0">
                  <p className="font-meta text-sm text-gold" data-testid={`artwork-price-${a.id}`}>
                    {a.price ? fmtPrice(a.price) : "—"}
                  </p>
                  <p
                    className={`font-meta text-[9px] uppercase tracking-[0.22em] mt-2 ${
                      a.availability === "prints" ? "text-ochre" : "text-mute"
                    }`}
                    data-testid={`artwork-availability-${a.id}`}
                  >
                    {a.availability === "prints" ? "Prints Available" : "Original Only"}
                  </p>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      )}
    </section>
  );
}
